import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { DialogProps } from './props.js'
import { CloseIcon } from './icons.js'

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  size = 'md',
  dismissible = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<Element | null>(null)

  /*
   * `onClose` is passed inline at almost every call site (`onClose={() =>
   * setOpen(false)}`), so it is a NEW function every render of the parent.
   * Typing into a field inside the dialog re-renders that parent on every
   * keystroke, which used to be in this effect's dependency array — so the
   * effect below tore down and re-ran on every keystroke too, and
   * `dialogRef.current.focus()` reclaimed focus from whatever input the
   * person was typing into, back to the dialog's own container. They then
   * had to click the field again for each letter.
   *
   * Refs sidestep this: the effect that does the real work (focus, scroll
   * lock, listeners) depends on `open` alone and so only runs when the
   * dialog actually opens or closes, while `onCloseRef`/`dismissibleRef`
   * keep the closures inside it reading the LATEST values without being a
   * reason to re-run it.
   */
  const onCloseRef = useRef(onClose)
  const dismissibleRef = useRef(dismissible)
  useEffect(() => {
    onCloseRef.current = onClose
    dismissibleRef.current = dismissible
  })

  useEffect(() => {
    if (!open) return

    // Store the currently focused element
    previousActiveElement.current = document.activeElement

    // Lock body scroll
    document.body.style.overflow = 'hidden'

    // Focus the dialog
    if (dialogRef.current) {
      dialogRef.current.focus()
    }

    // Handle keyboard and focus trap
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissibleRef.current) {
        onCloseRef.current()
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusableElements = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement
        const activeElement = document.activeElement

        if (e.shiftKey) {
          if (activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }
    }

    const handleScrimClick = (e: MouseEvent) => {
      if (dismissibleRef.current && e.target === e.currentTarget) {
        onCloseRef.current()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    if (dialogRef.current?.parentElement) {
      dialogRef.current.parentElement.addEventListener('click', handleScrimClick)
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
      if (dialogRef.current?.parentElement) {
        dialogRef.current.parentElement.removeEventListener('click', handleScrimClick)
      }
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus()
      }
    }
  }, [open])

  if (!open) return null

  const sizeClasses: Record<string, string> = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg',
  }

  return createPortal(
    <div
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      className="fixed inset-0 flex items-center justify-center p-3 sm:p-0 z-50 backdrop-blur-sm animate-fade-in"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        tabIndex={-1}
        className={`
          bg-surface-container border border-outline-variant rounded-xl shadow-elev-3
          w-full ${sizeClasses[size]}
          max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto
          animate-scale-in
        `}
      >
        <div className="px-5 pt-5 flex items-start justify-between">
          <div className="flex-1">
            <h2 id="dialog-title" className="text-title-lg text-on-surface">
              {title}
            </h2>
            {description && (
              <p className="text-body-sm text-on-surface-variant mt-1">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button
              onClick={onClose}
              className="text-on-surface hover:bg-surface-high p-2 rounded-full transition-colors interactive ms-4 flex-shrink-0"
              aria-label="Close dialog"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {children && <div className="px-5 py-4">{children}</div>}

        {actions && (
          <div className="px-5 pb-5 flex justify-end gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
